import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  NgModule,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { AbTestVersions } from '@core/services/abtest.webservice';
import { BreakpointsService } from '@core/services/breakpoints.service';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { HelpFormRoutingEnum } from '@features/help-form/help-form-routing.enum';
import { HelpsRoutingEnum } from '@features/helps/helps-routing.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterState } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { AbTestState } from '@stores/abtest/abtest.state';
import { EligibleSuggestionClear } from '@stores/eligible-suggestion/eligible-suggestion.action';
import { JwtState } from '@stores/jwt/jwt.state';
import { Logout } from '@stores/session/session.action';
import { SessionState } from '@stores/session/session.state';
import {
  ContactModalData,
  ContactModalService,
  WzbButtonModule,
  WzbHeaderComponent,
  WzbHeaderModule,
} from '@wizbii/angular-ui';
import { WINDOW } from '@wizbii/angular-utilities';
import { IdentityCard, LocaleEnum, UserOverview } from '@wizbii/models';
import { trackEvent } from '@wizbii/tracking';
import { SuggestionsWebservice } from '@wizbii/webservices';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  @Select(JwtState.isLogged)
  isLogged$!: Observable<boolean>;

  @Select(SessionState.isClient)
  isClient$!: Observable<boolean>;

  @Select(SessionState.identityCard)
  identityCard$!: Observable<IdentityCard>;

  @Select(SessionState.user)
  user$!: Observable<UserOverview>;

  @Select(AbTestState.version)
  version$!: Observable<AbTestVersions>;

  @ViewChild('wzbHeader')
  wzbHeader: WzbHeaderComponent | null = null;

  accountSignin = `${environment.urls.account.signIn}${this.window.location.origin}/${FeaturesRoutingEnum.Dashboard}`;
  accountSignup = `${environment.urls.account.signUp}${this.window.location.origin}/${FeaturesRoutingEnum.HelpForm}`;

  linkLegalityCgv = `https://legal.wizbii.com/${LocaleEnum.fr_FR}/cgv/fibii`;
  linkPdfCgv =
    'https://storage.googleapis.com/wizbii/money/Conditions%C2%A0Ge%CC%81ne%CC%81rales%20de%20Vente%20Wizbii%20Money%2012_2021.pdf';

  appId = environment.applicationId;
  apiDomain = environment.api.domain;
  contactEmailKey = environment.contactEmailKey.base;
  locale = environment.i18n.locale;

  trackEvent = trackEvent;
  trackingCategoryHome = 'Home Navigation';

  FeaturesRoutingEnum = FeaturesRoutingEnum;
  HelpsRoutingEnum = HelpsRoutingEnum;
  HelpFormRoutingEnum = HelpFormRoutingEnum;
  AbTestVersions = AbTestVersions;
  showWzbHeader$: Observable<boolean> = of(true);
  showWzbFooter$: Observable<boolean> = of(true);
  hasUserBeganForm$: Observable<boolean> = of(true);
  destroyed$ = new Subject<void>();

  processingDataClear$ = new BehaviorSubject<boolean>(false);

  get isHomeRoute(): boolean {
    return this.router.url === '/';
  }

  get isDashboardRoute(): boolean {
    return (
      this.router.url === `/${FeaturesRoutingEnum.Dashboard}` ||
      this.router.url === `/${FeaturesRoutingEnum.Beta}/${FeaturesRoutingEnum.Dashboard}`
    );
  }

  get isHelpsRoute(): boolean {
    return this.router.url === `/${HelpsRoutingEnum.Listing}`;
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  constructor(
    @Inject(WINDOW) private readonly window: any,
    private readonly store: Store,
    private readonly router: Router,
    readonly contactModalService: ContactModalService,
    private readonly suggestionsWebService: SuggestionsWebservice,
    private readonly breakpointsService: BreakpointsService
  ) {}

  ngOnInit(): void {
    this.showWzbHeader$ = this.store.select(RouterState.state).pipe(
      filter((state) => !!state),
      map((state: any) => (typeof state.data?.showWzbHeader === 'boolean' ? state.data?.showWzbHeader : true)),
      distinctUntilChanged()
    );
    this.showWzbFooter$ = this.store.select(RouterState.state).pipe(
      filter((state) => !!state),
      map((state: any) => (typeof state.data?.showWzbFooter === 'boolean' ? state.data?.showWzbFooter : true)),
      distinctUntilChanged()
    );
    this.hasUserBeganForm$ = this.store.select(JwtState.info).pipe(
      filter((info) => !!info),
      map((info) => info?.['user-id']),
      map((userId) => {
        if (!userId) {
          return undefined;
        }
        return this.suggestionsWebService.getQuestionsWithResponses(environment.suggestionSetId, userId, 'member');
      }),
      map((response) => !!response)
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  doLogout() {
    this.trackEvent(this.trackingCategoryHome, 'Click Logout');
    this.store.dispatch(new Logout());
  }

  openDialog(): void {
    trackEvent(this.trackingCategoryHome, 'Click Contactez-nous');
    this.getContactModalConfig()
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.contactModalService.openDialog({ ...data });
      });
  }

  getContactModalConfig(): Observable<ContactModalData> {
    return combineLatest([this.identityCard$, this.user$, this.isClient$]).pipe(
      map(([identityCard, user, isClient]) => ({
        appId: environment.applicationId,
        contactEmailKey: isClient ? environment.contactEmailKey.premium : environment.contactEmailKey.base,
        profile: {
          firstName: identityCard?.firstName,
          lastName: identityCard?.lastName,
          phone: identityCard?.mobile,
          email: user?.username,
        },
        locale: environment.i18n.locale,
      }))
    );
  }

  navigateAccount(url: string, trackingLabel: string) {
    this.trackEvent(this.trackingCategoryHome, trackingLabel);
    this.window.open(url, '_self');
  }

  resetForm() {
    this.processingDataClear$.next(true);
    this.trackEvent('Dashboard', 'Click Restart Header Money');

    this.store
      .select(JwtState.info)
      .pipe(
        takeUntil(this.destroyed$),
        filter((info) => !!info),
        map((info) => info?.['user-id']),
        switchMap((userId) => {
          if (!userId) {
            return of(undefined);
          }
          return this.suggestionsWebService.clearUserData(userId, 'member');
        }),
        switchMap(() => this.store.dispatch(new EligibleSuggestionClear()))
      )
      .subscribe(() => {
        this.processingDataClear$.next(false);
        this.router.navigate(['/', FeaturesRoutingEnum.HelpForm]);
      });
  }
}

@NgModule({
  imports: [CommonModule, RouterModule, WzbButtonModule, WzbHeaderModule, MatIconModule, MatButtonModule],
  exports: [DefaultLayoutComponent],
  declarations: [DefaultLayoutComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DefaultLayoutModule {}
